import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useTenantStore } from '../../stores/tenantStore';
import { useMessagingStore } from '../../stores/messagingStore';
import { Avatar } from '../common/Avatar';
import { ROUTES } from '../../utils/constants';

interface HeaderProps {
  tenant: string;
}

export function Header({ tenant }: HeaderProps) {
  const { user, logout } = useAuthStore();
  const { config } = useTenantStore();
  const { refreshUnreadCount } = useMessagingStore();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = React.useState(false);

  useEffect(() => {
    if (user) {
      refreshUnreadCount(tenant);
      // Refresh unread count every 30 seconds
      const interval = setInterval(() => refreshUnreadCount(tenant), 30000);
      return () => clearInterval(interval);
    }
  }, [tenant, user, refreshUnreadCount]);

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.login(tenant));
  };

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to={ROUTES.home(tenant)} className="flex items-center gap-2">
              {config?.branding.logoUrl ? (
                <img src={config.branding.logoUrl} alt={config.name} className="h-8" />
              ) : (
                <span className="text-xl font-bold text-primary-600">
                  {config?.name || 'OnlyCoach'}
                </span>
              )}
            </Link>

            {user && (
            <nav className="hidden md:flex ml-8 space-x-4">
              <Link
                to={ROUTES.home(tenant)}
                className={`px-3 py-2 text-sm font-medium ${
                  location.pathname === ROUTES.home(tenant)
                    ? 'text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Home
              </Link>
              {(user.role === 'coach' || user.role === 'admin') && (
                <>
                  <Link
                    to={ROUTES.coach.users(tenant)}
                    className={`px-3 py-2 text-sm font-medium ${
                      location.pathname.startsWith(ROUTES.coach.users(tenant))
                        ? 'text-primary-600 border-b-2 border-primary-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Users
                  </Link>
                  <Link
                    to={ROUTES.coach.groups(tenant)}
                    className={`px-3 py-2 text-sm font-medium ${
                      location.pathname.startsWith(ROUTES.coach.groups(tenant))
                        ? 'text-primary-600 border-b-2 border-primary-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Groups
                  </Link>
                </>
              )}
            </nav>
          )}
          </div>

          {user && (
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="flex items-center gap-2 p-1 rounded-lg hover:bg-gray-100"
              >
                <Avatar src={user.avatarUrl} name={user.name} size="sm" />
                <span className="hidden sm:block text-sm font-medium text-gray-700">
                  {user.name}
                </span>
                <svg
                  className="h-4 w-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                    <div className="py-1">
                      <Link
                        to={ROUTES.profile(tenant)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowMenu(false)}
                      >
                        Profile
                      </Link>
                      <Link
                        to={ROUTES.settings(tenant)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowMenu(false)}
                      >
                        Settings
                      </Link>
                      <hr className="my-1" />
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      >
                        Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
